/*
 * <fan-sign-event-form> — fan-sign event application (팬사인회 응모폼).
 * Ported from the Hydrogen storefront SignEventForm (app/components/SignEventForm). Handles the
 * Yes/No opt-in, expand/collapse, per-event-type conditional fields, validation (reference
 * regexes) and the Buy-now submit. See snippets/fan-sign-event-form.liquid.
 *
 * Submit mirrors the reference's `CartForm.ACTIONS.Create`: it builds a cart holding only this
 * product and checks out with it, so the sequence is
 *   POST /cart/clear.js -> POST /cart/add -> POST /cart/update (note + attributes) -> /checkout
 * The applicant info is the cart note (so it lands on the order as order.note) and the opt-in
 * flag / user id are cart attributes — the same payload shape as the reference's
 * processCartInputs. Buy-now therefore does NOT carry the rest of the shopper's cart to
 * checkout; that cart is dropped, exactly as a fresh Hydrogen cart would.
 */
if (!customElements.get('fan-sign-event-form')) {
  customElements.define(
    'fan-sign-event-form',
    class FanSignEventForm extends HTMLElement {
      constructor() {
        super();

        // Reference regexes (app/constants/signEvent.ts)
        this.EMAIL_REGEX = /^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i;
        this.DATE_OF_BIRTH_REGEX = /^\d{8}$/;
        this.PHONE_NUMBER_REGEX = /^[0-9]+$/;

        this.sectionId = this.dataset.sectionId;
        this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
        this.form = this.querySelector('form');
        this.card = this.querySelector('[data-fan-sign-card]');
        this.collapsible = this.querySelector('[data-fan-sign-collapsible]');
        this.attendingInputs = Array.from(this.querySelectorAll('.pdp__fan-sign-attending'));
        this.eventTypeSelect = this.querySelector('.pdp__fan-sign-event-type');
        this.conditionalGroups = Array.from(this.querySelectorAll('.pdp__fan-sign-conditional'));

        this.variantIdInput = this.querySelector('.pdp__fan-sign-variant-id');
        this.quantityInput = document.getElementById(`Quantity-${this.sectionId}`);

        this.submitButton = this.querySelector('.pdp__fan-sign-submit');
        this.submitSpinner = this.submitButton && this.submitButton.querySelector('.loading__spinner');
        this.submitting = false;

        this.strings = this.parseStrings();
        this.attending = null;

        this.bindEvents();
        this.setAttending(null);
      }

      parseStrings() {
        try {
          const raw = JSON.parse(this.querySelector('[data-fan-sign-strings]').textContent);
          // Liquid's `t` filter HTML-escapes output (e.g. ' -> &#39;), and a <script> body is
          // raw text the HTML parser does not entity-decode — so decode the values here.
          const decoder = document.createElement('textarea');
          const decode = (value) => {
            decoder.innerHTML = value;
            return decoder.value;
          };
          return Object.keys(raw).reduce((acc, key) => {
            acc[key] = typeof raw[key] === 'string' ? decode(raw[key]) : raw[key];
            return acc;
          }, {});
        } catch (e) {
          return {};
        }
      }

      bindEvents() {
        this.attendingInputs.forEach((input) => {
          input.addEventListener('change', () => this.setAttending(input.dataset.attending === 'true', input));
        });

        if (this.eventTypeSelect) {
          this.eventTypeSelect.addEventListener('change', () => this.onEventTypeChange());
        }

        // Clear a field's error as the shopper edits it.
        this.querySelectorAll('[data-fan-sign-field]').forEach((field) => {
          const evt = field.tagName === 'SELECT' ? 'change' : 'input';
          field.addEventListener(evt, () => this.clearError(this.errorKeyFor(field)));
        });

        this.form.addEventListener('submit', (event) => this.onSubmit(event));
      }

      /* ---- Attendance (Yes/No) --------------------------------------------- */

      // `opener` is the control the shopper used, so closing the login modal can hand focus back
      // to it rather than to the submit button at the bottom of the form.
      setAttending(value, opener) {
        // Clicking "Yes" while it is already the answer is a no-op in the reference
        // (AttendanceCheckboxGroup returns early), so the login modal must not reopen on it.
        const changed = value !== this.attending;

        this.attending = value; // true | false | null
        this.attendingInputs.forEach((input) => {
          const isYes = input.dataset.attending === 'true';
          input.checked = value !== null && ((isYes && value === true) || (!isYes && value === false));
        });

        if (value === true) {
          this.expand();
          if (changed) this.onYesRequiresLogin(opener);
        } else {
          this.collapse();
        }
        this.clearError('attending');
      }

      expand() {
        this.collapsible.hidden = false;
        this.card.classList.add('pdp__fan-sign-card--open');
        this.onEventTypeChange();
      }

      collapse() {
        this.collapsible.hidden = true;
        this.card.classList.remove('pdp__fan-sign-card--open');
        this.conditionalGroups.forEach((group) => (group.hidden = true));
      }

      /* ---- Conditional fields per event type ------------------------------- */

      // The reference resets every conditional field whenever the event type changes
      // (index.tsx useEffect -> resetField), so a value typed for one event type never
      // survives into another.
      onEventTypeChange() {
        if (!this.eventTypeSelect) return;
        const value = this.eventTypeSelect.value;
        this.conditionalGroups.forEach((group) => {
          const match = value !== '' && group.dataset.eventType === value;
          group.hidden = !match;
          if (!match) {
            group.querySelectorAll('[data-fan-sign-field]').forEach((field) => {
              field.value = '';
              this.clearError(this.errorKeyFor(field));
            });
          }
        });
      }

      /* ---- Login gate ------------------------------------------------------ */

      // Entering the draw is members-only. Liquid decides who is logged in (the Shopify customer
      // session) and says so with data-requires-login, so this never has to read a cookie —
      // see the header comment in snippets/fan-sign-event-form.liquid.
      requiresLogin() {
        return this.dataset.requiresLogin === 'true';
      }

      // Reference index.tsx raises the same modal from two places: the moment "Yes" is picked
      // (AttendanceCheckboxGroup's onYesClick) and again on submit. The form still expands and
      // still accepts input in between — the gate is on checking out, not on filling the form.
      //
      // The modal is a <modal-gate> (snippets/modal-gate.liquid), the same component the 19+ age
      // gate uses, so raising it is its open() rather than anything reimplemented here — that is
      // where the fallback for a <modal-box> that never upgraded lives. No url is passed: this
      // gate's own sign-in / sign-up links are the way onward, so there is nothing to release.
      onYesRequiresLogin(opener) {
        if (!this.requiresLogin()) return;

        const modal = document.getElementById(this.dataset.loginModal);
        if (!modal) return;

        const gate = modal.closest('modal-gate');
        if (typeof gate?.open === 'function') {
          gate.open(null, opener || this.submitButton);
        } else {
          // Only reachable if script-modal-gate.js itself failed to load, in which case the
          // native <dialog> is all that is left.
          modal.querySelector('dialog')?.showModal();
        }
      }

      /* ---- Validation ------------------------------------------------------ */

      getFieldValue(name) {
        const el = this.querySelector(`[data-fan-sign-field="${name}"]`);
        return el ? el.value.trim() : '';
      }

      maxLen(n) {
        return (this.strings.maxLength || '').replace('{max}', n);
      }

      errorKeyFor(field) {
        if (field.dataset.fanSignField === 'extra') return `extra:${field.dataset.extraKey}`;
        return field.dataset.fanSignField;
      }

      fieldByKey(key) {
        if (key.indexOf('extra:') === 0) return this.querySelector(`[data-extra-key="${key.slice(6)}"]`);
        return this.querySelector(`[data-fan-sign-field="${key}"]`);
      }

      // The <select> fields are visually hidden behind the custom dropdown (<select-dropdown>),
      // so focus belongs on the visible trigger instead.
      focusTarget(field) {
        if (!field) return null;
        const dropdown = field.closest('.pdp__fan-sign-select');
        return (dropdown && dropdown.querySelector('.dropdown-menu__trigger')) || field;
      }

      validate() {
        this.clearAllErrors();
        let valid = true;
        let firstInvalid = null;

        const fail = (key, message) => {
          this.showError(key, message);
          if (!firstInvalid) firstInvalid = this.fieldByKey(key);
          valid = false;
        };

        if (this.eventTypeSelect && !this.eventTypeSelect.value) {
          fail('eventType', this.strings.eventTypeRequired);
        }

        const name = this.getFieldValue('name');
        if (!name) fail('name', this.strings.nameRequired);
        else if (name.length > 100) fail('name', this.maxLen(100));

        const dob = this.getFieldValue('dayOfBirth');
        if (!this.DATE_OF_BIRTH_REGEX.test(dob)) fail('dayOfBirth', this.strings.dateOfBirthPattern);

        const phone = this.getFieldValue('phoneNum');
        if (!phone) fail('phoneNum', this.strings.phoneRequired);
        else if (!this.PHONE_NUMBER_REGEX.test(phone)) fail('phoneNum', this.strings.phoneForm);
        else if (phone.length > 30) fail('phoneNum', this.maxLen(30));

        const email = this.getFieldValue('email');
        if (!email) fail('email', this.strings.emailRequired);
        else if (!this.EMAIL_REGEX.test(email)) fail('email', this.strings.emailForm);

        this.conditionalGroups.forEach((group) => {
          if (group.hidden) return;
          group.querySelectorAll('[data-fan-sign-field="extra"]').forEach((el) => {
            const key = `extra:${el.dataset.extraKey}`;
            const val = el.value.trim();
            if (el.dataset.required === 'true' && !val) fail(key, el.dataset.requiredMessage || this.strings.nameRequired);
            else if (val.length > 50) fail(key, this.maxLen(50));
          });
        });

        const focusTarget = this.focusTarget(firstInvalid);
        if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
        return valid;
      }

      showError(key, message) {
        const errorEl = this.querySelector(`[data-error-for="${key}"]`);
        if (errorEl) {
          errorEl.textContent = message || '';
          errorEl.hidden = false;
        }
        const field = this.fieldByKey(key);
        if (field) {
          this.focusTarget(field).setAttribute('aria-invalid', 'true');
          const box = field.closest('.pdp__fan-sign-input-box, .pdp__fan-sign-select');
          if (box) box.classList.add('pdp__fan-sign-input-box--error');
        }
      }

      clearError(key) {
        const errorEl = this.querySelector(`[data-error-for="${key}"]`);
        if (errorEl) {
          errorEl.textContent = '';
          errorEl.hidden = true;
        }
        const field = this.fieldByKey(key);
        if (field) {
          this.focusTarget(field).removeAttribute('aria-invalid');
          const box = field.closest('.pdp__fan-sign-input-box, .pdp__fan-sign-select');
          if (box) box.classList.remove('pdp__fan-sign-input-box--error');
        }
      }

      clearAllErrors() {
        this.querySelectorAll('[data-error-for]').forEach((el) => {
          el.textContent = '';
          el.hidden = true;
        });
        this.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));
        this.querySelectorAll('.pdp__fan-sign-input-box--error').forEach((el) =>
          el.classList.remove('pdp__fan-sign-input-box--error')
        );
      }

      /* ---- Payload + submit ------------------------------------------------ */

      formatDate(str) {
        if (!str || str.length !== 8) return str || '';
        return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
      }

      buildApplication() {
        const phoneCode = this.getFieldValue('phoneCode').split('_')[0];
        const user = {
          name: this.getFieldValue('name'),
          dayOfBirth: this.formatDate(this.getFieldValue('dayOfBirth')),
          phoneNumber: `(+${phoneCode}) ${this.getFieldValue('phoneNum')}`,
          email: this.getFieldValue('email'),
        };

        const payload = { user };

        const eventType = this.eventTypeSelect ? this.eventTypeSelect.value : '';
        if (eventType) payload.eventType = eventType;

        const extra = {};
        this.conditionalGroups.forEach((group) => {
          if (group.hidden) return;
          group.querySelectorAll('[data-fan-sign-field="extra"]').forEach((el) => {
            const val = el.value.trim();
            if (val) extra[el.dataset.extraKey] = val;
          });
        });
        if (Object.keys(extra).length > 0) payload.extra = extra;

        return payload;
      }

      // TODO: the logged-in KiTbetter user id, once the auth backend lands (reference reads
      // kitbetterUserInfo.user_id). Sent empty for now so a stale attribute gets cleared.
      // script-gnb-user-type.js writes the user_id cookie once the API confirms the session,
      // which is what finally fills the reference's `_user_id` here. Routed through
      // KitAnalytics so the checkout pixel's two attributes stay defined in one place.
      userId() {
        return window.KitAnalytics?.userId() || '';
      }

      // The store promotion code the shopper arrived with (`?store_promotion_code=…`), lifted onto
      // the PDP section root by sections/main-product-kit.liquid because Liquid cannot read query
      // params. Reference: `...(storePromotionCode ? [{key: '_store_promotion_code', ...}] : [])`.
      storePromotionCode() {
        const root = this.closest('[data-store-promotion-code]');
        return (root && root.dataset.storePromotionCode) || '';
      }

      currentQuantity() {
        const parsed = parseInt(this.quantityInput ? this.quantityInput.value : '1', 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      /* ---- Cart routes ------------------------------------------------------ */

      cartRoutes() {
        const themeRoutes = window.routes || {};
        const cartUrl = themeRoutes.cart_url || '/cart';
        return {
          clear: `${cartUrl}/clear.js`,
          add: themeRoutes.cart_add_url || `${cartUrl}/add`,
          update: themeRoutes.cart_update_url || `${cartUrl}/update`,
        };
      }

      async postCart(url, body) {
        const config =
          typeof fetchConfig === 'function'
            ? fetchConfig()
            : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

        const response = await fetch(url, { ...config, body: JSON.stringify(body) });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error((data && (data.description || data.message)) || this.strings.cartError);
        }
        return data;
      }

      /* ---- Buy now ---------------------------------------------------------- */

      // Reference equivalent: processCartInputs + CartForm.ACTIONS.Create. Buy-now checks out
      // this product on its own, so whatever else was in the cart is dropped first.
      async buyNow() {
        const attending = this.attending === true;
        const routes = this.cartRoutes();

        await this.postCart(routes.clear, {});

        await this.postCart(routes.add, {
          items: [{ id: this.variantIdInput ? this.variantIdInput.value : '', quantity: this.currentQuantity() }],
        });

        // Buy-now leaves no later chance to attach the session id, so wait for it here.
        await window.KitAnalytics?.whenIdentified();

        // /cart/clear.js leaves the note and attributes behind, so both are always written —
        // empty note when not attending — to keep a previous application from leaking through.
        await this.postCart(routes.update, {
          note: attending ? JSON.stringify(this.buildApplication()) : '',
          attributes: {
            attending_fan_sign_event: attending ? 'true' : 'false',
            _store_promotion_code: this.storePromotionCode(),
            // Reference: app/components/SignEventForm/index.tsx:168 sends the same pair on
            // its CartForm.ACTIONS.Create. Buy-now skips the cart page, so this is the only
            // chance to put a session id on the cart before checkout reads it.
            ...(window.KitAnalytics?.cartAttributes() || { _user_id: this.userId() }),
          },
        });

        window.location.href = this.checkoutUrl;
      }

      setBusy(busy) {
        this.submitting = busy;
        if (!this.submitButton) return;
        this.submitButton.classList.toggle('loading', busy);
        if (busy) this.submitButton.setAttribute('aria-disabled', 'true');
        else this.submitButton.removeAttribute('aria-disabled');
        if (this.submitSpinner) this.submitSpinner.classList.toggle('hidden', !busy);
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitting) return;

        this.clearError('submit');

        if (this.attending === null) {
          this.showError('attending', this.strings.attendingRequired);
          if (this.attendingInputs[0]) this.attendingInputs[0].focus();
          return;
        }

        if (this.attending === true) {
          if (this.requiresLogin()) {
            this.onYesRequiresLogin();
            return;
          }
          if (!this.validate()) return;
        }

        this.setBusy(true);
        try {
          await this.buyNow();
        } catch (error) {
          this.setBusy(false);
          this.showError('submit', (error && error.message) || this.strings.cartError);
        }
      }
    }
  );
}
